import { EligibleRegisterGroup } from '@schooly/api';

import { groupEligibleForPeriod, groupEligibleForSession } from './registerEligibility';
import {
  isSessionScope,
  RegisterGroupIds,
  RegisterGroupTarget,
  RegisterPeriod,
  RegisterPeriodStructure,
  RegisterScope,
  RegisterSession,
} from '../types';

export const getAddedStructures = (
  groupIds: RegisterGroupIds,
  periods: RegisterPeriod[],
): Map<string, RegisterPeriodStructure[]> => {
  const byGroupId = new Map<string, Map<string, RegisterPeriodStructure>>();

  for (const period of periods) {
    for (const groupId of groupIds.periods[period.id] ?? []) {
      const structures = byGroupId.get(groupId) ?? new Map<string, RegisterPeriodStructure>();
      structures.set(period.structure.id, period.structure);
      byGroupId.set(groupId, structures);
    }
  }

  return new Map(
    [...byGroupId].map(([groupId, structures]) => [groupId, [...structures.values()]]),
  );
};

type GetCoupledStructurePeriodOptionsParams = {
  group: EligibleRegisterGroup;
  groupIds: RegisterGroupIds;
  session: RegisterSession;
  periods: RegisterPeriod[];
};

export const getCoupledStructurePeriodOptions = ({
  group,
  groupIds,
  session,
  periods,
}: GetCoupledStructurePeriodOptionsParams): RegisterPeriod[] => {
  const addedStructures = getAddedStructures(groupIds, periods).get(group.group_id) ?? [];
  const byStructure = new Map<string, RegisterPeriod>();

  for (const period of periods) {
    if (period.register_session !== session) continue;
    if (!groupEligibleForPeriod(group, period.id)) continue;
    if (addedStructures.length && !addedStructures.some(({ id }) => id === period.structure.id)) {
      continue;
    }
    if (byStructure.has(period.structure.id)) continue;

    byStructure.set(period.structure.id, period);
  }

  return [...byStructure.values()];
};

type GetCoupledTargetParams = {
  group: EligibleRegisterGroup;
  groupIds: RegisterGroupIds;
  scope: RegisterScope;
  periods: RegisterPeriod[];
};

export const getCoupledTarget = ({
  group,
  groupIds,
  scope,
  periods,
}: GetCoupledTargetParams): RegisterGroupTarget => {
  if (isSessionScope(scope)) {
    const periodId = getCoupledStructurePeriodOptions({
      group,
      groupIds,
      session: scope.session,
      periods,
    })[0]?.id;

    return { session: scope.session, periodId };
  }

  const session = periods.find((period) => period.id === scope.periodId)?.register_session;

  return {
    periodId: scope.periodId,
    session: session && groupEligibleForSession(group, session) ? session : undefined,
  };
};

type GetBlockingStructureParams = {
  groupId: string;
  addedStructures: Map<string, RegisterPeriodStructure[]>;
  scope: RegisterScope;
  periods: RegisterPeriod[];
};

export const getBlockingStructure = ({
  groupId,
  addedStructures,
  scope,
  periods,
}: GetBlockingStructureParams): RegisterPeriodStructure | undefined => {
  if (isSessionScope(scope)) return;

  const structureId = periods.find((period) => period.id === scope.periodId)?.structure.id;

  return (addedStructures.get(groupId) ?? []).find(({ id }) => id !== structureId);
};

export const mergeCoupledScopes = (
  scopes: RegisterScope[],
  periods: RegisterPeriod[],
): RegisterGroupTarget[] => {
  const sessions = scopes.flatMap((scope) => (isSessionScope(scope) ? [scope.session] : []));
  const periodIds = new Set(
    scopes.flatMap((scope) => (isSessionScope(scope) ? [] : [scope.periodId])),
  );

  const coupled = sessions.map((session) => {
    const periodId = periods.find(
      (period) => period.register_session === session && periodIds.has(period.id),
    )?.id;

    if (!periodId) return { session };

    periodIds.delete(periodId);

    return { session, periodId };
  });

  return [...coupled, ...[...periodIds].map((periodId) => ({ periodId }))];
};
